import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { API_PATHS } from '../config/api-paths';
import {
  CollectionAssignRequest,
  CollectionStatus,
  LenderCollectionsApi,
  OverdueAccountRow,
} from '../tokens/lender-collections.token';

/** {@code GET /api/v1/lender/portfolio} row (B6 LenderPortfolioRowDTO) — only the fields used here. */
interface PortfolioRowWire {
  loanReference: string;
  borrowerCui: string;
  borrowerName: string;
  currency: string;
  outstandingBalance: number;
  status: string;
  edName: string | null;
}

/** {@code GET/POST /api/v1/lender/collections/...} status wire (B6 LenderCollectionStatusDTO). */
interface CollectionStatusWire {
  loanReference: string;
  notices: {
    noticeReference: string;
    issuedAt: string;
    amountOverdue: number;
    status: string;
    debtCollector: string | null;
  }[];
}

/**
 * REST-only {@link LenderCollectionsApi} (EM-699 / F6 — EM-711).
 *
 * - Overdue accounts: synthesised from the lender portfolio (Rule 10 preferred pattern) —
 *   loans in {@code OVERDUE} / {@code DEFAULTED} status. The {@code ?status=} filter is
 *   passed through; defaulted rows are kept client-side.
 * - Assign / status: the dedicated B6 endpoints. Rides the Keycloak bearer.
 */
@Injectable()
export class LenderCollectionsRestService implements LenderCollectionsApi {
  private readonly http = inject(HttpClient);

  getOverdueAccounts(): Observable<OverdueAccountRow[]> {
    const params = new HttpParams().set('size', '500');
    return this.http.get<PortfolioRowWire[]>(API_PATHS.LENDER_SERVICING_PORTFOLIO, { params }).pipe(
      map(rows =>
        (rows ?? [])
          .filter(r => r.status === 'OVERDUE' || r.status === 'DEFAULTED')
          .map(r => ({
            loanReference: r.loanReference,
            borrowerCui: r.borrowerCui,
            borrowerName: r.borrowerName,
            edName: r.edName,
            amountDue: r.outstandingBalance,
            currency: r.currency,
            status: r.status,
          })),
      ),
    );
  }

  assign(request: CollectionAssignRequest): Observable<CollectionStatus> {
    return this.http
      .post<CollectionStatusWire>(`${API_PATHS.LENDER_COLLECTIONS}/assign`, request)
      .pipe(map(toStatus));
  }

  getStatus(accountId: string): Observable<CollectionStatus> {
    return this.http
      .get<CollectionStatusWire>(`${API_PATHS.LENDER_COLLECTIONS}/${encodeURIComponent(accountId)}`)
      .pipe(map(toStatus));
  }
}

function toStatus(w: CollectionStatusWire): CollectionStatus {
  return {
    loanReference: w.loanReference,
    notices: (w.notices ?? []).map(n => ({
      noticeReference: n.noticeReference,
      issuedAt: n.issuedAt,
      amountOverdue: n.amountOverdue,
      status: n.status,
      debtCollector: n.debtCollector,
    })),
  };
}
